/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { privacyAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { downloadBlob, entityLabel, formatDate } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../../components/common/NotificationProvider'
import { useNameOf } from '../../components/records/PersonInput'

const KIND_LABELS = { PERSON_EXPORT: 'Exported a person’s data', PERSON_ERASE: 'Erased a person’s data', WORKSPACE_EXPORT: 'Exported the workspace' }
const today = () => new Date().toISOString().slice(0, 10)

/**
 * Admins: answer a person's request to see or delete their data (find their records by email or
 * phone, export them, erase them for good), and export the whole workspace.
 */
const PrivacyPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [selected, setSelected] = useState([])
  const [reason, setReason] = useState('')
  const [erasing, setErasing] = useState(false)
  const nameOf = useNameOf()
  const log = useQuery({ queryKey: [tenantId, 'privacy', 'log'], queryFn: privacyAPI.log })
  const refreshLog = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'privacy', 'log'] })

  const search = useMutation({
    mutationFn: () => privacyAPI.search(query.trim()),
    onSuccess: (found) => { setResults(found); setSelected(found.map((m) => m.record.id)) },
  })
  const exportPerson = useMutation({
    mutationFn: () => privacyAPI.exportPerson(selected, reason.trim() || undefined),
    onSuccess: (blob) => { downloadBlob(blob, `personal-data-${today()}.zip`); refreshLog() },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const exportWorkspace = useMutation({
    mutationFn: privacyAPI.exportWorkspace,
    onSuccess: (blob) => { downloadBlob(blob, `workspace-export-${today()}.zip`); refreshLog() },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Privacy & data</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        When someone asks to see or delete the data you hold about them, find their records here.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>A person’s data</Typography>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); if (query.trim()) search.mutate() }} sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField size="small" fullWidth label="Email or phone number" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={!query.trim() || search.isPending}>Find</Button>
        </Box>
        {search.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(search.error)}</Alert>}
        {results && results.length === 0 && <Alert severity="info">No records hold this email or phone number.</Alert>}
        {results && results.length > 0 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.length === results.length} indeterminate={selected.length > 0 && selected.length < results.length}
                        onChange={(e) => setSelected(e.target.checked ? results.map((m) => m.record.id) : [])} inputProps={{ 'aria-label': 'Select all' }} />
                    </TableCell>
                    <TableCell>Record</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Found in</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(({ record, matchedOn }) => (
                    <TableRow key={record.id} hover>
                      <TableCell padding="checkbox"><Checkbox checked={selected.includes(record.id)} onChange={() => toggle(record.id)} inputProps={{ 'aria-label': `Select ${record.title}` }} /></TableCell>
                      <TableCell>
                        {record.deleted
                          ? <>{record.title} <Chip size="small" variant="outlined" label="deleted, still stored" sx={{ ml: 1 }} /></>
                          : <Link component={RouterLink} to={`/records/${record.entityType}/${record.id}`} target="_blank" rel="noopener">{record.title}</Link>}
                      </TableCell>
                      <TableCell>{entityLabel(record.entityType)}</TableCell>
                      <TableCell>{matchedOn.join(', ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField size="small" fullWidth label="Request reference (optional)" placeholder="e.g. Email from the customer, 16 Oct"
              value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 500 }} sx={{ mt: 2 }}
              helperText="Kept in the privacy log below. Don’t put the person’s details here." />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} disabled={!selected.length || exportPerson.isPending} onClick={() => exportPerson.mutate()}>
                {exportPerson.isPending ? 'Preparing…' : `Export ${selected.length} record${selected.length === 1 ? '' : 's'}`}
              </Button>
              <Button color="error" variant="outlined" disabled={!selected.length} onClick={() => setErasing(true)}>
                Erase {selected.length} record{selected.length === 1 ? '' : 's'}…
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The export holds the records with their notes, history, tasks, emails and files. Records linked to these (e.g. an account) aren’t included unless they show up here.
            </Typography>
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6">The whole workspace</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every record, note, task, email, file, member and form, as a ZIP of spreadsheets (CSV) and files. Large workspaces take a while.
        </Typography>
        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} disabled={exportWorkspace.isPending} onClick={() => exportWorkspace.mutate()}>
          {exportWorkspace.isPending ? 'Preparing export…' : 'Export workspace'}
        </Button>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Privacy log</Typography>
      {log.error && <Alert severity="error">{getErrorMessage(log.error)}</Alert>}
      {log.data?.length === 0 && <Typography variant="body2" color="text.secondary">No exports or erasures yet.</Typography>}
      {log.data?.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>When</TableCell><TableCell>What</TableCell><TableCell>Records</TableCell><TableCell>Reference</TableCell><TableCell>By</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {log.data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdOn)}</TableCell>
                  <TableCell>{KIND_LABELS[r.kind]}</TableCell>
                  <TableCell>{r.kind === 'WORKSPACE_EXPORT' ? 'all' : `${r.recordCount}${r.entityTypes ? ` (${r.entityTypes.split(', ').map(entityLabel).join(', ')})` : ''}`}</TableCell>
                  <TableCell>{r.reason || '—'}</TableCell>
                  <TableCell>{nameOf(r.requestedBy)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {erasing && (
        <EraseDialog count={selected.length} onClose={() => setErasing(false)}
          onErase={() => privacyAPI.erasePerson(selected, reason.trim() || undefined)}
          onDone={(r) => {
            setErasing(false); setResults(null); setSelected([]); setQuery(''); refreshLog()
            queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
            notify(`Erased ${r.records} record${r.records === 1 ? '' : 's'} with ${r.files} file${r.files === 1 ? '' : 's'} and ${r.tasks} task${r.tasks === 1 ? '' : 's'}`)
          }} />
      )}
    </Box>
  )
}

/** Typing ERASE confirms: this one can't be undone. */
const EraseDialog = ({ count, onClose, onErase, onDone }) => {
  const [typed, setTyped] = useState('')
  const erase = useMutation({ mutationFn: onErase, onSuccess: onDone })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Erase permanently?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {count} record{count === 1 ? '' : 's'} will be deleted for good, with their notes, history, tasks, emails and files.
          This can’t be undone. Backups keep them until they age out.
        </DialogContentText>
        {erase.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(erase.error)}</Alert>}
        <TextField fullWidth autoFocus label="Type ERASE to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={erase.isPending}>Cancel</Button>
        <Button color="error" variant="contained" disabled={typed !== 'ERASE' || erase.isPending} onClick={() => erase.mutate()}>
          {erase.isPending ? 'Erasing…' : 'Erase'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PrivacyPage
